export const physiologyModule = {
  id: "physiology",
  title: "Adrenal Gland Physiology",
  sections: [
    {
      type: "text",
      content: `
        <h3>Overview: The Three Zones and the Medulla</h3>
        <p>The adrenal gland has two functionally distinct regions — the <b class="purple-term">cortex</b> (outer) and the <b class="purple-term">medulla</b> (inner) — with completely different embryological origins, regulatory mechanisms and hormone products.</p>
        <p>The cortex is divided into three zones, each producing a different class of steroid hormone. A useful mnemonic for remembering the zones (from outside in) is <b>"GFR"</b> — just like the kidney's job:</p>
        <div class="mnemonic-card">
          <b>GFR</b> — Glomerulosa, Fasciculata, Reticularis<br/>
          <b>"Salt, Sugar, Sex"</b> — Mineralocorticoids, Glucocorticoids, Sex hormones<br/>
          <i>The deeper you go, the sweeter it gets.</i>
        </div>
        <div class="highlight-box">
          <b>Zona Glomerulosa</b> (outermost) → <b>Aldosterone</b> (mineralocorticoid) → Regulated by RAAS + K⁺<br/>
          <b>Zona Fasciculata</b> (middle) → <b>Cortisol</b> (glucocorticoid) → Regulated by ACTH (HPA axis)<br/>
          <b>Zona Reticularis</b> (innermost) → <b>Androgens</b> (DHEA, androstenedione) → Regulated by ACTH<br/>
          <b>Adrenal Medulla</b> → <b>Catecholamines</b> (adrenaline, noradrenaline) → Regulated by preganglionic sympathetic nerves
        </div>
      `,
      image: "/images/adrenalglands/adrenalglandphysiology.png",
    },
    {
      type: "question",
      text: "Moving from the outer cortex to the inner medulla, what is the correct order of the adrenal cortical zones?",
      options: [
        "Fasciculata → Glomerulosa → Reticularis",
        "Glomerulosa → Fasciculata → Reticularis",
        "Reticularis → Fasciculata → Glomerulosa",
        "Glomerulosa → Reticularis → Fasciculata",
      ],
      correct: 1,
      explanation:
        "From outer to inner: Zona Glomerulosa → Zona Fasciculata → Zona Reticularis → Medulla. The mnemonic 'GFR' (like glomerular filtration rate) helps remember the order, and 'Salt, Sugar, Sex' reminds you of each zone's hormone product.",
    },
    {
      type: "text",
      content: `
        <h3>Zona Glomerulosa — Aldosterone and the RAAS</h3>
        <p>The <b class="purple-term">zona glomerulosa</b> is the outermost cortical zone and the sole source of <b class="purple-term">aldosterone</b>, the body's principal mineralocorticoid. Unlike the other cortical zones, it is not significantly regulated by ACTH — instead, it is controlled by the <b class="purple-term">renin-angiotensin-aldosterone system (RAAS)</b> and by serum <b class="purple-term">potassium (K⁺)</b> levels.</p>

        <h3>The RAAS Pathway</h3>
        <p>The RAAS is a hormonal cascade that links blood pressure/volume to aldosterone secretion:</p>
        <ol>
          <li><b>Low blood pressure</b> (or low Na⁺, or sympathetic activation) is detected by the <b class="purple-term">juxtaglomerular apparatus (JGA)</b> of the kidney.</li>
          <li>The JGA releases <b class="purple-term">renin</b> — an enzyme — into the bloodstream.</li>
          <li>Renin cleaves <b class="purple-term">angiotensinogen</b> (produced by the liver) → <b class="purple-term">angiotensin I</b>.</li>
          <li><b class="purple-term">Angiotensin-converting enzyme (ACE)</b>, found predominantly in the <b>lungs</b>, converts angiotensin I → <b class="purple-term">angiotensin II</b>.</li>
          <li>Angiotensin II acts on the zona glomerulosa → stimulates <b>aldosterone</b> release.</li>
          <li>Aldosterone acts on the <b>distal tubule</b> and <b>collecting duct</b> of the nephron.</li>
        </ol>
        <div class="highlight-box">
          <b>RAAS cascade:</b> Low BP/volume → JGA releases renin → Angiotensinogen → Angiotensin I → (ACE in lungs) → Angiotensin II → Zona glomerulosa → Aldosterone → ↑Na⁺ reabsorption → ↑Blood volume → ↑Blood pressure
        </div>

        <h3>Actions of Aldosterone</h3>
        <p>Aldosterone binds to <b>intracellular mineralocorticoid receptors</b> in principal cells of the distal tubule and collecting duct. It upregulates the synthesis of <b class="purple-term">ENaC</b> (epithelial sodium channels) and <b class="purple-term">Na⁺/K⁺-ATPase</b> pumps on the basolateral side, leading to:</p>
        <ul>
          <li><b>↑ Na⁺ reabsorption</b> (water follows osmotically → ↑ blood volume → ↑ blood pressure)</li>
          <li><b>↑ K⁺ excretion</b> (for every Na⁺ saved, K⁺ is lost — clinically causes hypokalaemia in hyperaldosteronism)</li>
          <li><b>↑ H⁺ excretion</b> (via intercalated cells) → metabolic alkalosis</li>
        </ul>
        <div class="highlight-box">
          <b>Aldosterone effects:</b> ↑Na⁺ in, ↑K⁺ out, ↑H⁺ out → ↑Blood volume, hypokalaemia, metabolic alkalosis
        </div>
        <p><b>High K⁺ directly stimulates the zona glomerulosa</b> to release aldosterone, independent of renin — this is an important direct feedback mechanism to maintain K⁺ homeostasis.</p>
      `,
      image: "/images/adrenalglands/adrenalglandraas.png",
    },
    {
      type: "question",
      text: "A patient with Conn's syndrome (primary hyperaldosteronism) is likely to have which of the following electrolyte abnormalities?",
      options: [
        "Hyperkalaemia and metabolic acidosis",
        "Hypokalaemia and metabolic alkalosis",
        "Hypernatraemia and hyperkalaemia",
        "Hyponatraemia and metabolic alkalosis",
      ],
      correct: 1,
      explanation:
        "Excess aldosterone causes increased Na⁺ reabsorption (raising blood pressure), increased K⁺ excretion (hypokalaemia), and increased H⁺ excretion (metabolic alkalosis). Hypokalaemia + metabolic alkalosis + hypertension is the classic triad of primary hyperaldosteronism (Conn's syndrome).",
    },
    {
      type: "question",
      text: "Where does angiotensin-converting enzyme (ACE) primarily act to convert angiotensin I to angiotensin II?",
      options: [
        "Liver",
        "Kidneys",
        "Lungs",
        "Adrenal glands",
      ],
      correct: 2,
      explanation:
        "ACE is found on the luminal surface of vascular endothelial cells, most densely in the pulmonary circulation (lungs). As blood passes through the lungs, ACE converts angiotensin I (10 amino acids) to the active angiotensin II (8 amino acids). This is why ACE inhibitors (e.g. ramipril) are used to treat hypertension and heart failure — they block this conversion, reducing aldosterone release and blood pressure.",
    },
    {
      type: "text",
      content: `
        <h3>Zona Fasciculata — Cortisol and the HPA Axis</h3>
        <p>The <b class="purple-term">zona fasciculata</b> is the largest zone and produces <b class="purple-term">cortisol</b>, the body's principal glucocorticoid. It is regulated by the <b class="purple-term">hypothalamic-pituitary-adrenal (HPA) axis</b>.</p>

        <h3>The HPA Axis</h3>
        <ol>
          <li>Physical or psychological stress is detected by the <b class="purple-term">hypothalamus</b>, which releases <b class="purple-term">CRH</b> (corticotropin-releasing hormone) into the hypothalamic-pituitary portal blood.</li>
          <li>CRH stimulates the <b class="purple-term">anterior pituitary</b> to release <b class="purple-term">ACTH</b> (adrenocorticotropic hormone) into the systemic circulation.</li>
          <li>ACTH binds to receptors on zona fasciculata cells, stimulating <b>cortisol synthesis and secretion</b>.</li>
          <li>Rising cortisol levels exert <b class="purple-term">negative feedback</b> on both the hypothalamus (suppressing CRH) and the anterior pituitary (suppressing ACTH), forming a self-regulating loop.</li>
        </ol>
        <div class="highlight-box">
          <b>HPA axis:</b> Stress → Hypothalamus (CRH) → Anterior pituitary (ACTH) → Zona fasciculata (Cortisol) → Negative feedback on hypothalamus and pituitary<br/>
          Cortisol is highest in the <b>early morning</b> (around 8 am) and lowest at <b>midnight</b> — this is the <b>diurnal rhythm</b>.
        </div>
      `,
      image: "/images/adrenalglands/adrenalglandhpa.png",
    },
    {
      type: "text",
      content: `
        <h3>Actions of Cortisol</h3>
        <p>Cortisol is a steroid hormone that crosses cell membranes and binds to intracellular glucocorticoid receptors (GRs). The receptor-hormone complex then acts as a transcription factor, influencing hundreds of genes. Its effects are widespread:</p>

        <h3>Metabolic effects — raising blood glucose</h3>
        <ul>
          <li><b class="purple-term">Gluconeogenesis</b>: promotes glucose synthesis in the liver from amino acids, glycerol and lactate → raises blood glucose</li>
          <li><b class="purple-term">Glycogen synthesis</b>: promotes hepatic glycogen storage</li>
          <li><b class="purple-term">Protein catabolism</b>: breaks down muscle and connective tissue proteins → provides amino acids for gluconeogenesis → causes muscle wasting and thin skin (seen in Cushing's)</li>
          <li><b class="purple-term">Lipolysis</b>: mobilises free fatty acids from peripheral adipose tissue → but paradoxically promotes central (truncal + facial) fat deposition (the mechanism is incompletely understood)</li>
          <li><b>Insulin resistance</b>: glucocorticoids antagonise insulin action → chronic exposure → type 2 diabetes</li>
        </ul>

        <h3>Anti-inflammatory and immunosuppressive effects</h3>
        <ul>
          <li>Inhibits <b class="purple-term">phospholipase A₂</b> (via lipocortin) → blocks arachidonic acid release → reduces synthesis of prostaglandins and leukotrienes</li>
          <li>Inhibits <b class="purple-term">NF-κB</b> → reduces transcription of pro-inflammatory cytokines (IL-1, IL-6, TNF-α)</li>
          <li>Reduces vascular permeability → less oedema</li>
          <li>Suppresses T-cell proliferation and antibody production</li>
          <li>Causes <b class="purple-term">eosinopenia</b> and <b class="purple-term">lymphopenia</b> (redistributes lymphocytes to lymphoid tissue)</li>
          <li>Causes <b class="purple-term">neutrophilia</b> (demargination from vessel walls) — so the white cell count rises but with reduced function</li>
        </ul>

        <h3>Other effects</h3>
        <ul>
          <li><b>Bone</b>: inhibits osteoblasts, promotes osteoclast activity → osteoporosis with chronic exposure</li>
          <li><b>Skin</b>: reduces collagen synthesis → thin, fragile skin; striae</li>
          <li><b>Fluid</b>: at high levels, cortisol has weak mineralocorticoid activity → Na⁺ retention, K⁺ loss</li>
          <li><b>CNS</b>: affects mood (euphoria at low levels, depression/psychosis at high levels); suppresses sleep; can raise intraocular pressure</li>
          <li><b>Growth</b>: suppresses GH axis and inhibits epiphyseal growth plates (children on long-term steroids have growth retardation)</li>
        </ul>

        <div class="highlight-box">
          <b>Cortisol summary:</b><br/>
          ↑Blood glucose (gluconeogenesis, insulin resistance) · ↓Inflammation · ↓Immunity<br/>
          ↓Bone · ↓Collagen · Central fat redistribution · Mood effects
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient on long-term oral prednisolone develops truncal obesity, striae and easy bruising. What is the mechanism behind their thin, fragile skin?",
      options: [
        "Increased phospholipase A₂ activity causing prostaglandin overproduction",
        "Cortisol-induced suppression of collagen synthesis",
        "Insulin resistance reducing glucose uptake in keratinocytes",
        "ACTH stimulation causing hyperpigmentation",
      ],
      correct: 1,
      explanation:
        "Glucocorticoids inhibit fibroblast activity and reduce collagen synthesis. This leads to thinning of the dermis, easy bruising, poor wound healing, and the characteristic purple striae (stretch marks) of Cushing's syndrome. The skin becomes fragile because it lacks the collagen scaffold that normally gives it tensile strength.",
    },
    {
      type: "question",
      text: "A surgical patient on long-term corticosteroids for rheumatoid arthritis is about to undergo an elective hip replacement. What is the most important perioperative concern related to their steroid use?",
      options: [
        "Risk of hyperkalaemia due to aldosterone suppression",
        "Adrenal suppression leading to adrenal crisis if steroids are not adequately supplemented",
        "Increased risk of pulmonary embolism due to hypercoagulability",
        "Raised intraocular pressure causing acute glaucoma",
      ],
      correct: 1,
      explanation:
        "Long-term exogenous corticosteroids suppress the HPA axis via negative feedback. The adrenal glands atrophy and cannot mount a normal cortisol response to surgical stress. This can precipitate an Addisonian (adrenal) crisis — hypotension, hypoglycaemia, shock — if adequate steroid cover is not given perioperatively. This is why patients on long-term steroids require 'sick day rules' and perioperative hydrocortisone supplementation.",
    },
    {
      type: "question",
      text: "Which of the following best describes the negative feedback mechanism of the HPA axis?",
      options: [
        "High cortisol stimulates both CRH from the hypothalamus and ACTH from the anterior pituitary",
        "High cortisol suppresses CRH from the hypothalamus and ACTH from the anterior pituitary",
        "High ACTH suppresses cortisol production in the zona glomerulosa",
        "Low cortisol stimulates the posterior pituitary to release ADH",
      ],
      correct: 1,
      explanation:
        "Cortisol exerts negative feedback on both the hypothalamus (reducing CRH secretion) and the anterior pituitary (reducing ACTH secretion). This self-limiting loop prevents excessive cortisol production. This is the basis of the overnight dexamethasone suppression test: in a normal person, exogenous dexamethasone suppresses ACTH and cortisol; failure to suppress suggests autonomous cortisol production (e.g. adrenal adenoma) or a pituitary ACTH-secreting tumour.",
    },
    {
      type: "text",
      content: `
        <h3>Zona Reticularis — Adrenal Androgens</h3>
        <p>The <b class="purple-term">zona reticularis</b> is the innermost cortical zone and produces <b class="purple-term">adrenal androgens</b>, primarily <b class="purple-term">DHEA</b> (dehydroepiandrosterone) and <b class="purple-term">androstenedione</b>. Like the zona fasciculata, it is regulated by <b>ACTH</b>.</p>

        <h3>What are adrenal androgens?</h3>
        <p>DHEA and androstenedione are <b>weak androgens</b> — far less potent than testosterone. They serve as precursors that are converted to more potent androgens (testosterone) and oestrogens in peripheral tissues (skin, fat, gonads).</p>
        <ul>
          <li>In <b>males</b>: adrenal androgens contribute only a small fraction of total androgen effect — the testes produce the vast majority of testosterone. Their contribution is usually clinically insignificant.</li>
          <li>In <b>females</b>: adrenal androgens are the <b>major source</b> of androgens. They drive <b class="purple-term">adrenarche</b> (pubic and axillary hair growth at puberty) and contribute to libido. In post-menopausal women, peripheral conversion of adrenal androgens to oestrone in adipose tissue becomes a significant source of oestrogen.</li>
        </ul>

        <h3>Adrenarche</h3>
        <p><b class="purple-term">Adrenarche</b> refers to the maturation of the zona reticularis and the onset of adrenal androgen secretion, which typically occurs between <b>6–8 years of age</b> — before the gonadal axis activates (gonadarche). It is responsible for:</p>
        <ul>
          <li>Pubic and axillary hair (pubarche)</li>
          <li>Adult body odour (apocrine sweat gland development)</li>
          <li>Early acne</li>
        </ul>
        <div class="highlight-box">
          Zona reticularis → DHEA + androstenedione → weak androgens → peripheral conversion → oestrogens and testosterone<br/>
          Regulated by ACTH · Important in females · Drives adrenarche from ~age 7
        </div>
      `,
    },
    {
      type: "question",
      text: "A 7-year-old girl develops pubic hair and mild acne, but has no breast development and her bone age is only slightly advanced. Her gonadotropins (LH, FSH) are prepubertal. What is the most likely explanation?",
      options: [
        "Precocious puberty driven by premature gonadotropin release",
        "Premature adrenarche — early maturation of the zona reticularis causing excess adrenal androgens",
        "Congenital adrenal hyperplasia causing excess cortisol",
        "An androgen-secreting ovarian tumour",
      ],
      correct: 1,
      explanation:
        "Premature adrenarche is characterised by pubic/axillary hair, mild acne and adult body odour in a child aged 6–8, with prepubertal gonadotropins (LH/FSH are low, ruling out central precocious puberty). It results from early maturation of the zona reticularis with increased DHEA production. Bone age is only mildly advanced. It is usually benign and a variant of normal development, but congenital adrenal hyperplasia should be excluded.",
    },
    {
      type: "text",
      content: `
        <h3>Adrenal Medulla — Catecholamine Synthesis</h3>
        <p>The <b class="purple-term">adrenal medulla</b> is composed of <b class="purple-term">chromaffin cells</b> (derived from neural crest cells) and produces the catecholamines <b class="purple-term">adrenaline (epinephrine)</b> (~80%) and <b class="purple-term">noradrenaline (norepinephrine)</b> (~20%), releasing them directly into the bloodstream as hormones.</p>

        <h3>Catecholamine Synthesis Pathway</h3>
        <p>The synthesis starts from the amino acid <b class="purple-term">tyrosine</b> and proceeds through four enzymatic steps:</p>
        <ol>
          <li><b class="purple-term">Tyrosine</b> → <b class="purple-term">L-DOPA</b> via <b>tyrosine hydroxylase</b> (rate-limiting step)</li>
          <li><b class="purple-term">L-DOPA</b> → <b class="purple-term">Dopamine</b> via <b>DOPA decarboxylase (aromatic L-amino acid decarboxylase)</b></li>
          <li><b class="purple-term">Dopamine</b> → <b class="purple-term">Noradrenaline</b> via <b>dopamine β-hydroxylase</b> (inside secretory vesicles)</li>
          <li><b class="purple-term">Noradrenaline</b> → <b class="purple-term">Adrenaline</b> via <b>PNMT</b> (phenylethanolamine N-methyltransferase) — this step occurs only in the adrenal medulla</li>
        </ol>
        <div class="mnemonic-card">
          <b>"Tyrants Don't Need Adrenaline"</b><br/>
          <b>T</b>yrosine → <b>D</b>OPA → <b>D</b>opamine → <b>N</b>oradrenaline → <b>A</b>drenaline
        </div>
        <div class="highlight-box">
          <b>Rate-limiting step:</b> Tyrosine hydroxylase (Tyrosine → L-DOPA)<br/>
          <b>Step unique to adrenal medulla:</b> PNMT (Noradrenaline → Adrenaline)<br/>
          <b>Why only in medulla?</b> PNMT expression requires <b>high cortisol concentrations</b> — the medulla is bathed in cortisol-rich blood from the cortex via a portal system. Cortisol induces PNMT transcription.
        </div>
        <p>This means that if the portal blood supply between cortex and medulla is disrupted, adrenaline synthesis falls (noradrenaline continues, as it doesn't need PNMT). This is a key physiological relationship between the two layers of the adrenal gland.</p>
      `,
      image: "/images/adrenalglands/adrenalglandcatecholamines.png",
    },
    {
      type: "question",
      text: "Which enzyme catalyses the rate-limiting step in catecholamine synthesis?",
      options: [
        "DOPA decarboxylase",
        "Dopamine β-hydroxylase",
        "Tyrosine hydroxylase",
        "Phenylethanolamine N-methyltransferase (PNMT)",
      ],
      correct: 2,
      explanation:
        "Tyrosine hydroxylase converts tyrosine to L-DOPA and is the rate-limiting step in catecholamine biosynthesis. It is inhibited by its end-products (catecholamines) via negative feedback. This is why drugs like metyrosine (a tyrosine hydroxylase inhibitor) can be used to reduce catecholamine production in phaeochromocytoma.",
    },
    {
      type: "question",
      text: "Why is conversion of noradrenaline to adrenaline restricted to the adrenal medulla?",
      options: [
        "Dopamine β-hydroxylase is only expressed in the adrenal medulla",
        "PNMT, the enzyme required for the conversion, is induced by high local cortisol concentrations found in the medullary portal blood supply from the cortex",
        "Noradrenaline cannot cross the blood-brain barrier to reach other tissues",
        "The adrenal medulla lacks vesicles needed for noradrenaline storage",
      ],
      correct: 1,
      explanation:
        "PNMT (phenylethanolamine N-methyltransferase), which converts noradrenaline to adrenaline, requires high concentrations of cortisol for its induction. The adrenal medulla receives cortisol-rich blood from a portal system draining the cortex, providing the supraphysiological cortisol concentrations needed to maintain PNMT expression. Peripheral sympathetic neurons cannot convert noradrenaline to adrenaline because they lack this cortisol exposure.",
    },
    {
      type: "text",
      content: `
        <h3>Actions of Catecholamines — The Fight-or-Flight Response</h3>
        <p>Adrenaline and noradrenaline act on <b class="purple-term">adrenergic receptors</b> (α₁, α₂, β₁, β₂, β₃). The key distinction is that <b>adrenaline</b> acts on both α and β receptors, while <b>noradrenaline</b> predominantly acts on α receptors (with less β₂ activity). This accounts for their different cardiovascular effects.</p>

        <h3>Key physiological effects during stress</h3>
        <ul>
          <li><b>Heart (β₁)</b>: ↑ Heart rate (chronotropy), ↑ Force of contraction (inotropy) → ↑ Cardiac output</li>
          <li><b>Blood vessels (α₁ and β₂)</b>:
            <ul>
              <li>Adrenaline: vasoconstriction in skin/viscera (α₁) but vasodilation in skeletal muscle (β₂) → blood shunted to working muscles</li>
              <li>Noradrenaline: predominantly vasoconstriction (α₁) → ↑ peripheral resistance → ↑ blood pressure</li>
            </ul>
          </li>
          <li><b>Lungs (β₂)</b>: bronchodilation → ↑ airway diameter → ↑ oxygen delivery</li>
          <li><b>Eyes (α₁)</b>: pupil dilation (mydriasis) → ↑ visual field</li>
          <li><b>Liver (β₂)</b>: glycogenolysis → ↑ blood glucose</li>
          <li><b>Pancreas (α₂)</b>: inhibits insulin secretion → ↑ blood glucose (glucose spared for muscles and brain)</li>
          <li><b>Adipose tissue (β₃)</b>: lipolysis → ↑ free fatty acids → alternative fuel</li>
          <li><b>Gut (α₁ + β₂)</b>: reduced peristalsis (non-essential during emergency)</li>
          <li><b>Skin</b>: sweating, piloerection</li>
        </ul>
        <div class="highlight-box">
          <b>Fight-or-flight:</b> ↑HR · ↑CO · Bronchodilation · Pupil dilation · ↑Blood glucose (glycogenolysis + ↓insulin) · Vasoconstriction in skin/gut · Vasodilation in muscle
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient presents to A&E in anaphylactic shock with severe hypotension and bronchospasm. You administer intramuscular adrenaline (epinephrine). Which TWO receptor actions make adrenaline the drug of choice in this situation?",
      options: [
        "α₁ (vasoconstriction → ↑blood pressure) and β₂ (bronchodilation)",
        "β₁ (↑heart rate) and α₂ (↓noradrenaline release)",
        "β₃ (lipolysis) and α₁ (pupil dilation)",
        "β₁ (↑heart rate) and β₃ (↑lipolysis)",
      ],
      correct: 0,
      explanation:
        "In anaphylaxis, the two life-threatening problems are hypotension (from massive vasodilation and vascular leak) and bronchospasm. Adrenaline addresses both: α₁ receptor activation causes vasoconstriction, raising blood pressure; β₂ receptor activation causes bronchodilation, relieving bronchospasm. Adrenaline also has β₁ effects (increasing heart rate and cardiac output) which further counteract hypotension. This is why adrenaline is the first-line treatment for anaphylaxis.",
    },
    {
      type: "flashcard",
      front: "Compare the zones of the adrenal cortex: hormone, regulation and key function.",
      back: `<table style="width:100%; border-collapse: collapse; font-size: 0.9em;">
        <tr style="background:#f3f4f6">
          <th style="padding:4px 8px; text-align:left">Zone</th>
          <th style="padding:4px 8px">Hormone</th>
          <th style="padding:4px 8px">Regulation</th>
          <th style="padding:4px 8px">Key effect</th>
        </tr>
        <tr>
          <td style="padding:4px 8px"><b>Glomerulosa</b></td>
          <td style="padding:4px 8px; text-align:center">Aldosterone</td>
          <td style="padding:4px 8px; text-align:center">RAAS + K⁺</td>
          <td style="padding:4px 8px; text-align:center">↑Na⁺ retention, ↑K⁺ excretion → ↑BP</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 8px"><b>Fasciculata</b></td>
          <td style="padding:4px 8px; text-align:center">Cortisol</td>
          <td style="padding:4px 8px; text-align:center">ACTH (HPA axis)</td>
          <td style="padding:4px 8px; text-align:center">↑Blood glucose, anti-inflammatory, immunosuppressive</td>
        </tr>
        <tr>
          <td style="padding:4px 8px"><b>Reticularis</b></td>
          <td style="padding:4px 8px; text-align:center">DHEA, androstenedione</td>
          <td style="padding:4px 8px; text-align:center">ACTH</td>
          <td style="padding:4px 8px; text-align:center">Adrenarche; precursor to sex steroids</td>
        </tr>
        <tr style="background:#f9fafb">
          <td style="padding:4px 8px"><b>Medulla</b></td>
          <td style="padding:4px 8px; text-align:center">Adrenaline (~80%), Noradrenaline (~20%)</td>
          <td style="padding:4px 8px; text-align:center">Preganglionic sympathetic (T5–T11)</td>
          <td style="padding:4px 8px; text-align:center">Fight-or-flight: ↑HR, ↑BP, bronchodilation, ↑glucose</td>
        </tr>
      </table>`,
      hint: "GFR = Glomerulosa, Fasciculata, Reticularis | Salt, Sugar, Sex | Medulla = catecholamines",
    },
    {
      type: "flashcard",
      front: "What is the catecholamine synthesis pathway and which step is rate-limiting?",
      back: `<p style="margin-bottom:8px"><b>Synthesis pathway (Tyrosine → Adrenaline):</b></p>
      <ol style="margin:0; padding-left:20px; font-size:0.9em;">
        <li><b>Tyrosine</b> → L-DOPA via <b>Tyrosine hydroxylase</b> ⭐ <i>(rate-limiting)</i></li>
        <li><b>L-DOPA</b> → Dopamine via <b>DOPA decarboxylase</b></li>
        <li><b>Dopamine</b> → Noradrenaline via <b>Dopamine β-hydroxylase</b> (in vesicles)</li>
        <li><b>Noradrenaline</b> → Adrenaline via <b>PNMT</b> (needs high cortisol — medulla only)</li>
      </ol>
      <p style="margin-top:8px"><b>Mnemonic:</b> "Tyrants Don't Need Adrenaline"<br/>
      Tyrosine · DOPA · Dopamine · Noradrenaline · Adrenaline</p>`,
      hint: "Rate-limiting = Tyrosine hydroxylase | PNMT requires cortisol | Step 4 unique to adrenal medulla",
    },
  ],
};
